import React from "react";
import { Package, Users, IndianRupee, TrendingUp, ShoppingCart, Activity, Star } from 'lucide-react';

interface StatCardsProps {
  stats: {
    totalSales: number;
    todaySales: number;
    yesterdaySales: number;
    totalOrders: number;
    totalProducts: number;
    totalUsers: number;
    pendingOrders: number;
    completedOrders: number;
    lowStockProducts: number;
    avgOrderValue: number;
    conversionRate: number;
    topSellingCategory: string;
    monthlyGrowth: number;
    dailyOrdersAvg: number;
  };
}

const StatCards: React.FC<StatCardsProps> = ({ stats }) => (
  <>
    {/* Enhanced Stats Grid */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      <StatCard
        title="Total Sales"
        value={`₹${stats.totalSales.toLocaleString()}`}
        icon={IndianRupee}
        trend={stats.monthlyGrowth > 0 ? "up" : stats.monthlyGrowth < 0 ? "down" : "neutral"}
        trendValue={Math.abs(stats.monthlyGrowth)}
        color="green"
      />
      <StatCard
        title="Today's Sales"
        value={`₹${stats.todaySales.toLocaleString()}`}
        icon={TrendingUp}
        
        color="blue"
      />
      <StatCard
        title="Total Orders"
        value={stats.totalOrders.toLocaleString()}
        icon={ShoppingCart}
        subtitle={`${stats.pendingOrders} pending`}
        color="purple"
      />
      <StatCard
        title="Avg Order Value"
        value={`₹${Math.round(stats.avgOrderValue).toLocaleString()}`}
        icon={IndianRupee}
        subtitle="Per transaction"
        color="indigo"
      />
    </div>
    {/* Secondary Stats */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
      <StatCard
        title="Total Products"
        value={stats.totalProducts.toLocaleString()}
        icon={Package}
        subtitle={`${stats.lowStockProducts} low stock`}
        color="yellow"
      />
      <StatCard
        title="Total Users"
        value={stats.totalUsers.toLocaleString()}
        icon={Users}
        subtitle={`${stats.conversionRate.toFixed(1)}% conversion rate`}
        color="green"
      />
      <StatCard
        title="Completed Orders"
        value={stats.completedOrders.toLocaleString()}
        icon={Activity}
        color="blue"
      />
      <StatCard
        title="Top Category"
        value={stats.topSellingCategory || 'N/A'}
        icon={Star}
        color="purple"
      />
    </div>
  </>
);

// Reuse the StatCard component from the dashboard file
// If not available, define a minimal version here
interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ElementType;
  trend?: "up" | "down" | "neutral";
  trendValue?: number;
  color?: string;
  subtitle?: string;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon: Icon, trend, trendValue, color = "primary", subtitle, onClick }) => (
  <div
    className={`bg-white rounded-lg shadow p-6 flex items-center cursor-pointer border border-gray-100 hover:shadow-md transition ${onClick ? "hover:bg-gray-50" : ""}`}
    onClick={onClick}
  >
    <div className={`w-12 h-12 flex items-center justify-center rounded-full bg-${color}-100 text-${color}-600 text-2xl mr-4`}>
      <Icon className={`w-7 h-7`} />
    </div>
    <div>
      <div className="text-lg font-bold text-gray-900">{value}</div>
      <div className="text-sm text-gray-600 font-medium">{title}</div>
      {subtitle && <div className="text-xs text-gray-500 mt-1">{subtitle}</div>}
      {trend && (
        <div className="flex items-center mt-1 text-xs">
          {trend === "up" && <span className="text-green-600">▲</span>}
          {trend === "down" && <span className="text-red-600">▼</span>}
          {trendValue !== undefined && <span className="ml-1">{trendValue}%</span>}
        </div>
      )}
    </div>
  </div>
);

export default StatCards;
